import {AppConfig, BaseAppProps, BaseAppState} from "shared/BaseApp";
import React, {createRef, ReactElement, RefObject} from 'react';
import {createTheme, ThemeOptions} from "@mui/material";
import {DEFAULT_THEME_OPTIONS} from "../shared/colors";
import {Main} from "./Main";
import {FIREBASE_CONFIG} from "../consts";
import {ProvisionedApp} from "../shared/ProvisionedApp";
import {ProvisionedAppConfig} from "../shared/types";
import "../res/css/App.css";
import {PathComponent} from "../index";
import {BORDER_RADIUS, CARD_SHADOW, PD_MD, PD_XSM} from "../shared/dimens";
import {BLACK, BLUE, DARK_YELLOW, LIGHT_YELLOW, YELLOW} from "./styles";

export type AppProps = BaseAppProps & {}

type AppState = BaseAppState & {}

export const COLOR_PRIMARY = BLACK;
export const COLOR_SECONDARY = BLUE;

export const COLOR_BG1 = YELLOW;
export const COLOR_BG2 = YELLOW;
export const COLOR_BG3 = DARK_YELLOW;
export const COLOR_BG4 = LIGHT_YELLOW;

export const THEME_OPTIONS: ThemeOptions = {
  ...DEFAULT_THEME_OPTIONS,
  components: {
    ...DEFAULT_THEME_OPTIONS.components,
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          borderRadius: 48,
          whiteSpace: "nowrap",
          boxShadow: CARD_SHADOW,
          paddingTop: PD_XSM,
          paddingBottom: PD_XSM,
          paddingLeft: PD_MD,
          paddingRight: PD_MD,
          textTransform: "none",
          // fontWeight: "bold",
        },
      }
    },
  },
  typography: {
    h1: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h2: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h3: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h4: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h5: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h6: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    fontFamily: "Poppins, sans-serif",
    fontSize: 15,
  },
  shape: {
    borderRadius: 24,
  },
  palette: {
    primary: {
      main: COLOR_PRIMARY,
      contrastText: "#fff",
    },
    secondary: {
      main: COLOR_SECONDARY,
      contrastText: "#fff",
    },
    background: {
      default: COLOR_BG1,
      paper: COLOR_BG4,
    }
  },
};

const THEME = createTheme(THEME_OPTIONS);

export class AppRefs {

  constructor(
    readonly searchText: RefObject<HTMLInputElement>,
    readonly localAudioRenderer: RefObject<HTMLAudioElement>,
    readonly localAudioElement: ReactElement,
    readonly localVideoRenderer: RefObject<HTMLVideoElement>,
    readonly localVideoElement: ReactElement,
    readonly remoteAudioRenderer: RefObject<HTMLAudioElement>,
    readonly remoteAudioElement: ReactElement,
    readonly remoteVideoRenderer: RefObject<HTMLVideoElement>,
    readonly remoteVideoElement: ReactElement) {
  }
}

export class App extends ProvisionedApp<AppProps, AppState> {

  private static appRefs: AppRefs;

  static getAppRefs(): AppRefs {
    if (!App.appRefs) {
      App.appRefs = App.createAppRefs();
    }
    return App.appRefs;
  }

  private static createAudioElement(ref: RefObject<HTMLAudioElement>, muted: boolean): ReactElement {
    return <audio ref={ref} muted={muted}/>;
  }

  private static createVideoElement(ref: RefObject<HTMLVideoElement>): ReactElement {
    return <video ref={ref} autoPlay playsInline muted
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: BORDER_RADIUS,
                  }}></video>;
  }

  private static createAppRefs() {
    const localAudioRenderer = createRef<HTMLAudioElement>();
    const localVideoRenderer = createRef<HTMLVideoElement>();
    const remoteAudioRenderer = createRef<HTMLAudioElement>();
    const remoteVideoRenderer = createRef<HTMLVideoElement>();
    return new AppRefs(
      createRef<HTMLInputElement>(),
      localAudioRenderer,
      App.createAudioElement(localAudioRenderer, true),
      localVideoRenderer,
      App.createVideoElement(localVideoRenderer),
      remoteAudioRenderer,
      App.createAudioElement(remoteAudioRenderer, false),
      remoteVideoRenderer,
      App.createVideoElement(remoteVideoRenderer));
  }

  getProvisionedAppId(): string {
    return "heybuzzme";
  }

  getProvisionedAppConfig(): ProvisionedAppConfig | null {
    return null;
  }

  onCreateAppConfig(): AppConfig {
    return {
      name: "Hey Buzz Me!",
      icon: "/icon.png",
      logo: "/logo192.png",
      stamp: "/stamp.svg",
      stampHeight: 160,
      defaultUserImage: "/images/default_user.png",
      loginConfig: {
        noLogin: true,
        guestLogin: true,
      },
      theme: THEME,
      firebaseConfig: {options: FIREBASE_CONFIG},
    };
  }

  static nestedPaths(): PathComponent[] {
    return Main.nestedPaths();
  }

  protected renderMain(): ReactElement {
    return <Main path={this.props.path}/>;
  }
}
